/**
 * Post-capture verification for slide exports.
 *
 * WebKit (Safari desktop and iOS) has a race in SVG foreignObject
 * rasterization: the outer SVG is drawn to canvas before large inline
 * data-URI images finish decoding, so those images silently come out as
 * blank rectangles — text renders, images don't. The decode completes
 * moments later, which is why an immediate re-capture succeeds.
 *
 * Instead of sniffing user agents, we verify the produced PNG: for every
 * <img> inside the captured element we sample the corresponding region of
 * the output bitmap. A region with (near) zero variance means the image was
 * not painted, and the caller should re-capture (the decode cache is warm
 * by then, so the retry renders correctly).
 */

/** Sampled-region variance below this value counts as "not painted". */
const BLANK_VARIANCE_THRESHOLD = 6;

/** Max number of pixels sampled per image region. */
const MAX_SAMPLES = 2000;

/**
 * Returns true when at least one <img> region inside `element` came out as a
 * uniform (unpainted) area in the captured `blob`. Never throws — on any
 * internal failure it returns false so exports are never blocked by the
 * verification itself. False positives (a genuinely solid-color image) only
 * cost an extra capture pass.
 */
export async function hasBlankImageRegions(
  blob: Blob,
  element: HTMLElement
): Promise<boolean> {
  const imgs = Array.from(element.querySelectorAll('img')).filter(
    (img) => img.complete && img.naturalWidth > 0
  );
  if (imgs.length === 0) return false;

  const elRect = element.getBoundingClientRect();
  if (elRect.width === 0 || elRect.height === 0) return false;

  const url = URL.createObjectURL(blob);
  const canvas = document.createElement('canvas');
  try {
    const bitmap = new Image();
    await new Promise<void>((resolve, reject) => {
      bitmap.onload = () => resolve();
      bitmap.onerror = () => reject(new Error('capture blob decode failed'));
      bitmap.src = url;
    });

    canvas.width = bitmap.naturalWidth;
    canvas.height = bitmap.naturalHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;
    ctx.drawImage(bitmap, 0, 0);

    // Map viewport coordinates to output-bitmap pixels. Using ratios keeps
    // this correct even when the element sits inside a CSS transform: scale.
    const toPxX = (x: number) => ((x - elRect.left) / elRect.width) * canvas.width;
    const toPxY = (y: number) => ((y - elRect.top) / elRect.height) * canvas.height;

    return imgs.some((img) => {
      const r = img.getBoundingClientRect();
      if (r.width < 16 || r.height < 16) return false;

      // Sample the central 60% so rounded corners / borders don't skew stats.
      const x0 = Math.max(0, Math.round(toPxX(r.left + r.width * 0.2)));
      const y0 = Math.max(0, Math.round(toPxY(r.top + r.height * 0.2)));
      const x1 = Math.min(canvas.width, Math.round(toPxX(r.left + r.width * 0.8)));
      const y1 = Math.min(canvas.height, Math.round(toPxY(r.top + r.height * 0.8)));
      const w = x1 - x0;
      const h = y1 - y0;
      if (w < 4 || h < 4) return false;

      const data = ctx.getImageData(x0, y0, w, h).data;
      const total = w * h;
      const step = Math.max(1, Math.floor(total / MAX_SAMPLES));
      let n = 0;
      let sum = 0;
      let sumSq = 0;
      for (let i = 0; i < total; i += step) {
        const o = i * 4;
        const lum = (data[o] + data[o + 1] + data[o + 2]) / 3;
        sum += lum;
        sumSq += lum * lum;
        n++;
      }
      if (n === 0) return false;
      const mean = sum / n;
      const variance = sumSq / n - mean * mean;
      return variance < BLANK_VARIANCE_THRESHOLD;
    });
  } catch {
    return false;
  } finally {
    canvas.width = 0;
    canvas.height = 0;
    URL.revokeObjectURL(url);
  }
}
